import React, { useState, useRef } from 'react';
import { fileAPI } from '../../utils/axios';
import { handleFileUploadError, showErrorNotification } from '../../utils/errorHandler';
import * as S from './FileCardInput.style';

const FileCardInput = ({ onUpload, onCancel }) => {
  const [fileName, setFileName] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // 허용된 파일 확장자
  const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'pdf', 'doc', 'docx'];
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const validateFile = (file) => {
    if (!file) {
      return { isValid: false, message: '파일을 선택해주세요.' };
    }

    // 파일 크기 검증
    if (file.size > MAX_FILE_SIZE) {
      return { 
        isValid: false, 
        message: `파일 크기가 너무 큽니다. 최대 100MB까지 업로드 가능합니다. (선택된 파일: ${(file.size / 1024 / 1024).toFixed(2)}MB)` 
      };
    }

    // 파일 확장자 검증
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return { 
        isValid: false, 
        message: `지원하지 않는 파일 형식입니다. (${ALLOWED_EXTENSIONS.join(', ')} 파일만 업로드 가능)` 
      };
    }

    return { isValid: true };
  };

  const handleFileNameChange = (event) => {
    setFileName(event.target.value);
  };

  const handleFileDescriptionChange = (event) => {
    setFileDescription(event.target.value);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      const validation = validateFile(file);
      if (!validation.isValid) {
        alert(validation.message);
        // 파일 입력 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      
      // 파일명이 비어있으면 파일명에서 확장자를 제거한 이름을 자동 입력
      if (!fileName.trim()) {
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
        setFileName(nameWithoutExtension);
      }
    }
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    // 입력 검증
    if (!fileName.trim()) {
      alert('파일명을 입력해주세요.');
      return;
    }
    
    if (!selectedFile) {
      alert('파일을 선택해주세요.');
      return;
    }

    // 파일 재검증
    const validation = validateFile(selectedFile);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      console.log('[FILE_UPLOAD]', 'Starting upload:', {
        filename: fileName.trim(),
        originalName: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        description: fileDescription.trim()
      });

      // 파일 업로드 API 호출
      const result = await fileAPI.upload(selectedFile, fileName, fileDescription.trim());

      if (result.success) {
        console.log('[FILE_UPLOAD]', 'Upload successful:', result.data);
        
        // 업로드된 파일 정보를 부모 컴포넌트에 전달
        const uploadedFile = {
          id: result.data.file_id,
          name: fileName.trim(),
          fileName: result.data.filename,
          fileDescription: fileDescription.trim(),
          isActive: false,
          file_size: result.data.file_size,
          file_type: result.data.file_type,
          uploaded_at: new Date().toISOString()
        };

        onUpload?.(uploadedFile);
        
        // 성공 메시지
        alert('파일이 성공적으로 업로드되었습니다.');
        
        // 폼 초기화
        resetForm();
        
      } else {
        throw new Error(result.message || '파일 업로드에 실패했습니다.');
      }

    } catch (error) {
      console.error('[FILE_UPLOAD]', 'Upload error:', error);
      
      // 파일 업로드 전용 에러 처리
      const errorInfo = handleFileUploadError(error, selectedFile.name);
      alert(`업로드 실패: ${errorInfo.message}`);
      
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    if (isUploading) {
      const confirmed = window.confirm('파일 업로드가 진행 중입니다. 취소하시겠습니까?');
      if (!confirmed) return;
    }

    resetForm();
    onCancel?.();
  };

  const resetForm = () => {
    setFileName('');
    setFileDescription('');
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <S.InputContainer>
      {/* 업로드 진행 중 오버레이 */}
      {isUploading && (
        <S.UploadOverlay>
          <S.UploadProgress>
            <S.ProgressBar progress={uploadProgress} />
            <S.ProgressText>파일 업로드 중... {uploadProgress}%</S.ProgressText>
          </S.UploadProgress>
        </S.UploadOverlay>
      )}

      <S.FieldGroup>
        <S.FieldLabel>File Name *</S.FieldLabel>
        <S.TextInput
          type="text"
          value={fileName}
          onChange={handleFileNameChange}
          placeholder="File Name"
          disabled={isUploading}
          maxLength={100}
        />
        <S.FieldHint>최대 100자까지 입력 가능합니다.</S.FieldHint>
      </S.FieldGroup>

      <S.FieldGroup>
        <S.FieldLabel>File Description</S.FieldLabel>
        <S.TextArea
          value={fileDescription}
          onChange={handleFileDescriptionChange}
          placeholder="파일에 대한 설명을 입력하세요 (선택사항)"
          disabled={isUploading}
          maxLength={500}
        />
        <S.FieldHint>최대 500자까지 입력 가능합니다.</S.FieldHint>
      </S.FieldGroup>

      <S.FieldGroup>
        <S.FieldLabel>Select File *</S.FieldLabel>
        <S.FileUploadArea onClick={handleFileUploadClick} disabled={isUploading}>
          <S.UploadIcon>📄</S.UploadIcon>
          {selectedFile ? (
            <S.FileInfo>
              <S.UploadText>{selectedFile.name}</S.UploadText>
              <S.FileDetails>
                크기: {formatFileSize(selectedFile.size)} | 
                형식: {selectedFile.type || 'Unknown'}
              </S.FileDetails>
            </S.FileInfo>
          ) : (
            <S.UploadText>
              파일을 선택하거나 여기에 드래그하세요
            </S.UploadText>
          )}
          <S.HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
            onChange={handleFileSelect}
            disabled={isUploading}
          />
        </S.FileUploadArea>
        <S.FieldHint>
          지원 형식: Excel (.xlsx, .xls), CSV (.csv), PDF (.pdf), Word (.doc, .docx)<br/>
          최대 파일 크기: 100MB
        </S.FieldHint>
      </S.FieldGroup>

      <S.ButtonGroup>
        <S.CancelButton onClick={handleCancel} disabled={isUploading}>
          취소
        </S.CancelButton>
        <S.UploadButton 
          onClick={handleUpload} 
          disabled={isUploading || !fileName.trim() || !selectedFile}
        >
          {isUploading ? '업로드 중...' : '업로드'}
        </S.UploadButton>
      </S.ButtonGroup>
    </S.InputContainer>
  );
};

export default FileCardInput;